// formatActivitySystemMessages.js

import {
  formatActivityFieldChangeMessage,
  resolveActivityFieldDef,
} from 'node/activity/utils/formatActivityFieldChange';
import {
  isIgnoredActivityFieldPath,
  isIgnoredActivitySummaryText,
  shouldOmitActivityFieldChange,
} from 'node/activity/utils/placeholderTitleRename';
import { getResolvedFields } from 'utilities/NodeType';

const CORE_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  tags: 'Tags',
  coverUrl: 'Cover',
  permissions: 'Permissions',
};

const toSentenceCase = (value) => {
  if (!value) return '';
  const normalized = String(value).replace(/[_-]/g, ' ').trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * Activity copy sits after the actor name ("Kevin …"), so start lowercase.
 *
 * @param {unknown} text
 * @returns {string}
 */
export function asActivityContinuation(text) {
  const normalized = String(text || '').trim();
  if (!normalized) return '';
  return normalized.charAt(0).toLowerCase() + normalized.slice(1);
};

/**
 * Stored accomplishment continuation (no actor, no trailing period).
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeAccomplishmentPhrase(value) {
  const text = String(value || '').trim()
    .replace(/^["'`]+|["'`]+$/g, '')
    .replace(/\.+$/, '')
    .trim();
  if (!text) return '';
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * @param {object} change
 * @returns {string}
 */
export function formatChecklistActivityMessage(change) {
  const changeKind = String(change?.changeKind || '').trim();
  const itemText = String(change?.itemText || '').trim();
  const checklistTitle = String(change?.checklistTitle || '').trim() || 'Checklist';

  if (changeKind === 'item_completed') {
    const phrase = normalizeAccomplishmentPhrase(change?.accomplishmentPhrase);
    if (phrase) return `${phrase}.`;
    return itemText ? `completed "${itemText}".` : 'completed a checklist item.';
  }

  if (changeKind === 'item_reopened') {
    return itemText ? `reopened "${itemText}".` : 'reopened a checklist item.';
  }

  if (changeKind === 'item_added') {
    return itemText ? `added checklist item "${itemText}".` : `added an item to ${checklistTitle}.`;
  }
  if (changeKind === 'item_removed') {
    return itemText ? `removed checklist item "${itemText}".` : `removed an item from ${checklistTitle}.`;
  }
  if (changeKind === 'item_text_changed') {
    return itemText ? `updated checklist item "${itemText}".` : `updated a checklist item in ${checklistTitle}.`;
  }
  if (changeKind === 'items_reordered') {
    return `reordered items in ${checklistTitle}.`;
  }
  if (changeKind === 'assignees_changed') {
    return itemText ? `updated assignees for "${itemText}".` : `updated checklist assignees in ${checklistTitle}.`;
  }
  if (changeKind === 'checklist_created') {
    return `created ${checklistTitle}.`;
  }
  if (changeKind === 'checklist_deleted') {
    return `deleted ${checklistTitle}.`;
  }
  if (changeKind === 'checklist_renamed') {
    return `renamed ${checklistTitle}.`;
  }

  return `updated ${checklistTitle}.`;
}

/**
 * @param {object} event
 * @returns {string[]}
 */
export function formatChecklistActivityMessages(event) {
  const changes = Array.isArray(event?.changes) ? event.changes : [];
  const messages = changes
    .map((change) => formatChecklistActivityMessage(change))
    .filter(Boolean);
  return [...new Set(messages)];
}

/**
 * Label-only fallback when no newValue is available (legacy summaries / hints).
 *
 * @param {unknown} fieldLabel
 * @returns {string}
 */
function formatFieldUpdateMessage(fieldLabel) {
  return formatActivityFieldChangeMessage({ fieldLabel, hasNewValue: false });
}

/**
 * Normalize legacy stored copy ("Updated Description.") into continuation form.
 *
 * @param {unknown} text
 * @returns {string}
 */
function normalizeActivityUpdateCopy(text) {
  const normalized = String(text || '').trim();
  if (!normalized) return '';

  const match = normalized.match(/^updated\s+(.+?)\.?$/i);
  if (match) {
    return formatFieldUpdateMessage(match[1]);
  }

  return asActivityContinuation(normalized);
}

const withIndefiniteArticle = (noun) => {
  const normalized = String(noun || '').trim().toLowerCase() || 'item';
  const article = /^[aeiou]/i.test(normalized) ? 'an' : 'a';
  return `${article} ${normalized}`;
};

/**
 * Quiet relationship line after the actor name (title shown as a bullet below).
 *
 * @param {object} event
 * @returns {string}
 */
export function formatRelationshipActivityMessage(event) {
  const change = Array.isArray(event?.changes) ? event.changes[0] : null;
  const typeLabel = String(
    change?.relatedNodeTypeLabel
    || event?.relatedNodeTypeLabel
    || ''
  ).trim();
  const typeKey = String(
    change?.relatedNodeType
    || event?.relatedNode?.type
    || ''
  ).trim();

  let noun = typeLabel;
  if (!noun && typeKey && !isOpaqueActivityId(typeKey)) {
    noun = toSentenceCase(typeKey.replace(/[_-]/g, ' ').toLowerCase());
  }
  if (!noun) noun = 'item';

  const target = withIndefiniteArticle(noun);
  const changeKind = String(change?.changeKind || '').trim();
  if (changeKind === 'edge_unlinked') {
    return `removed ${target}.`;
  }
  return `added ${target}.`;
}

/**
 * Story beat continuation after the actor name.
 *
 * @param {object} event
 * @returns {string}
 */
export function formatStoryBeatActivityMessage(event) {
  const stored = String(event?.message || '').trim();
  if (stored) return asActivityContinuation(stored);

  const subtype = String(event?.storySubtype || '').trim();
  if (subtype === 'connect') {
    const connector = String(event?.connector || 'a source').trim() || 'a source';
    return `connected ${connector} to this project.`;
  }
  if (subtype === 'digest') {
    const count = Number(event?.digestCount);
    const connector = String(event?.connector || '').trim().toLowerCase();
    const reason = String(event?.reason || '').trim();
    const wasWere = count === 1 ? 'was' : 'were';
    if (Number.isFinite(count) && count > 0) {
      if (connector === 'drive' || connector === 'google_drive') {
        return reason
          ? `${wasWere} added to ${reason}.`
          : `${wasWere} added in Google Drive.`;
      }
      if (connector === 'gmail') {
        return `${wasWere} brought into this project from Gmail.`;
      }
      if (connector === 'calendar' || connector === 'google_calendar') {
        return reason
          ? `${wasWere} scheduled on ${reason}.`
          : `${wasWere} scheduled.`;
      }
      if (connector === 'clickup') {
        return reason
          ? `${wasWere} created in ${reason}.`
          : `${wasWere} created in ClickUp.`;
      }
      return count === 1
        ? 'was brought into this project.'
        : 'were brought into this project.';
    }
    return 'were brought into this project.';
  }
  return 'added something from the outside world.';
}

/**
 * Preview chips stored on day-digest story beats.
 *
 * @param {object} event
 * @returns {Array<{ key: string, title: string, type: string, typeLabel: string }>}
 */
export function resolveStoryDigestPreviews(event) {
  const raw = event?.raw && typeof event.raw === 'object' ? event.raw : {};
  const rows = Array.isArray(raw.digestPreviews) ? raw.digestPreviews : [];
  return rows
    .map((row) => {
      const key = String(row?.key || '').trim();
      if (!key) return null;
      return {
        key,
        title: String(row?.title || 'Related item').trim() || 'Related item',
        type: String(row?.type || '').trim(),
        typeLabel: String(row?.typeLabel || '').trim(),
      };
    })
    .filter(Boolean);
}

/**
 * True when a path/label segment looks like a Firestore auto-id (or data_<id>).
 * Custom node types often use these as type keys, which must not surface in Activity copy.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOpaqueActivityId(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return false;
  if (/^data_[a-zA-Z0-9]{12,}$/i.test(normalized)) return true;
  if (/^data\s+[a-zA-Z0-9]{12,}$/i.test(normalized)) return true;
  return /^[a-zA-Z0-9]{16,}$/.test(normalized);
}

/**
 * @param {object|null|undefined} node
 * @param {object|null|undefined} [typeDef] - Optional node type definition (for label).
 * @returns {Record<string, string>}
 */
export function buildActivityFieldLabelLookup(node, typeDef = null) {
  const lookup = { ...CORE_FIELD_LABELS };
  if (!node?.type) return lookup;

  const typeKey = String(node.type).trim();
  const dataKey = `data_${typeKey.toLowerCase()}`;
  const typeLabel = String(typeDef?.label || '').trim();
  if (typeLabel) {
    lookup[dataKey] = typeLabel;
  }

  const fields = getResolvedFields(node.type, { includeHidden: true, excludeSecured: true });
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (!field) return;
    const baseLabel = field.label || '';
    const sourceKey = String(field.source || node.type).toLowerCase();
    const fieldDataKey = `data_${sourceKey}`;
    const fieldName = field.name ? field.name.trim() : '';

    if (typeof field.type === 'string' && field.type.endsWith('_form')) {
      if (baseLabel) lookup[fieldDataKey] = baseLabel;
      return;
    }

    if (fieldName && baseLabel) {
      lookup[`${fieldDataKey}.${fieldName}`] = baseLabel;
    }
  });

  return lookup;
}

/**
 * @param {Record<string, string>} fieldLabels
 * @param {unknown} path
 * @returns {string}
 */
export function resolveActivityFieldLabel(fieldLabels, path) {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return 'Field';
  if (fieldLabels?.[normalizedPath]) return fieldLabels[normalizedPath];

  const segments = normalizedPath.split('.');
  for (let end = segments.length - 1; end > 0; end -= 1) {
    const candidatePath = segments.slice(0, end).join('.');
    if (fieldLabels?.[candidatePath]) return fieldLabels[candidatePath];
  }

  const fallbackSegment = segments.pop() || path;
  if (isOpaqueActivityId(fallbackSegment) || isOpaqueActivityId(normalizedPath)) {
    // Prefer the type/form bag label when the path is data_<typeKey>.
    if (fieldLabels?.[normalizedPath]) return fieldLabels[normalizedPath];
    return 'details';
  }

  return toSentenceCase(fallbackSegment) || 'Field';
}

/**
 * @param {object} event
 * @returns {string}
 */
export function getActivityActorName(event) {
  if (event?.actorName) return event.actorName;
  const summary = event?.user;
  if (!summary) return 'Someone';
  if (summary.title) return summary.title;
  if (summary.user?.name) return summary.user.name;
  if (summary.name) return summary.name;
  if (summary.userKey) return summary.userKey;
  return 'Someone';
}

/**
 * @param {object} params
 * @param {object} params.event
 * @param {Record<string, string>} params.fieldLabels
 * @param {Record<string, object>} [params.fieldDefs]
 * @param {string} [params.typeLabel]
 * @returns {string[]}
 */
export function formatActivitySystemMessages({
  event,
  fieldLabels = {},
  fieldDefs = {},
  typeLabel = '',
} = {}) {
  if (!event) return ['updated a field.'];

  const kind = String(event.kind || '').trim();
  const type = String(event.type || '').trim();
  if (kind === 'created' || type === 'node_created') {
    const label = String(event.nodeTypeLabel || typeLabel || '').trim() || 'item';
    return [`created this ${label}.`];
  }

  if (kind === 'relationship-changed' || type === 'node_relationship_changed') {
    return [formatRelationshipActivityMessage(event)];
  }

  if (kind === 'story_beat' || type === 'node_story_beat') {
    return [formatStoryBeatActivityMessage(event)];
  }

  if (kind === 'checklist-changed' || type === 'node_checklist_changed') {
    const checklistMessages = formatChecklistActivityMessages(event);
    if (checklistMessages.length > 0) return checklistMessages;
  }

  const effectiveTypeLabel = String(typeLabel || event.nodeTypeLabel || '').trim();

  const fieldChangeMessages = Array.isArray(event?.changes)
    ? event.changes
      .filter((change) => !shouldOmitActivityFieldChange(change, effectiveTypeLabel))
      .map((change) => {
        const fieldPath = String(change?.fieldPath || change?.field || event?.field || '').trim();
        if (!fieldPath) return '';

        const storedLabel = String(change?.fieldLabel || '').trim();
        const resolvedFieldLabel = (
          storedLabel
          && storedLabel !== fieldPath
          && !isOpaqueActivityId(storedLabel)
        )
          ? storedLabel
          : resolveActivityFieldLabel(fieldLabels, fieldPath);

        if (!resolvedFieldLabel || isOpaqueActivityId(resolvedFieldLabel)) {
          return formatFieldUpdateMessage('details');
        }

        const hasNewValue = Object.prototype.hasOwnProperty.call(change || {}, 'newValue')
          || Object.prototype.hasOwnProperty.call(event || {}, 'newValue');
        const newValue = Object.prototype.hasOwnProperty.call(change || {}, 'newValue')
          ? change.newValue
          : event?.newValue;

        return formatActivityFieldChangeMessage({
          fieldLabel: resolvedFieldLabel,
          newValue,
          hasNewValue,
          fieldDef: resolveActivityFieldDef(fieldDefs, fieldPath),
          fieldPath,
        });
      })
      .filter(Boolean)
    : [];

  if (fieldChangeMessages.length > 0) {
    return [...new Set(fieldChangeMessages)];
  }

  if (
    Array.isArray(event?.changes)
    && event.changes.length > 0
    && event.changes.every((change) => shouldOmitActivityFieldChange(change, effectiveTypeLabel))
  ) {
    return [];
  }

  const promptHints = Array.isArray(event?.promptHints)
    ? event.promptHints.map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];
  if (promptHints.length > 0) {
    const visibleHints = promptHints.filter((hint) => !isIgnoredActivitySummaryText(hint));
    if (visibleHints.length === 0) return [];
    return visibleHints.map((hint) => (
      isOpaqueActivityId(hint)
        ? formatFieldUpdateMessage('details')
        : normalizeActivityUpdateCopy(hint)
    ));
  }

  const changeSummaries = Array.isArray(event?.changeSummaries)
    ? event.changeSummaries.map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];
  if (changeSummaries.length > 0) {
    const visibleSummaries = changeSummaries.filter((summary) => !isIgnoredActivitySummaryText(summary));
    if (visibleSummaries.length === 0) return [];
    return visibleSummaries.map((summary) => {
      if (/data_[a-zA-Z0-9]{12,}/i.test(summary) || /Data\s+[a-zA-Z0-9]{12,}/.test(summary)) {
        const fieldLabel = resolveActivityFieldLabel(fieldLabels, event.field);
        if (fieldLabel && !isOpaqueActivityId(fieldLabel)) {
          return formatFieldUpdateMessage(fieldLabel);
        }
        return formatFieldUpdateMessage('details');
      }
      return normalizeActivityUpdateCopy(summary);
    });
  }

  if (isIgnoredActivityFieldPath(event.field || event.fieldPath)) {
    return [];
  }

  const fieldLabel = resolveActivityFieldLabel(fieldLabels, event.field);
  const normalizedLabel = String(fieldLabel || 'field').trim().toLowerCase() || 'field';
  return [
    `updated ${normalizedLabel.startsWith('the ') ? normalizedLabel : `the ${normalizedLabel}`}.`,
  ];
}
